import React from "react";
import {Alert, AlertTitle, Box, Table, TableBody, TableCell, TableHead, TableRow, Typography} from "@mui/material";
import {Occupation, OccupationPersonMonth} from "arteco-api-client-ts";

export interface OccupationImputationProps {
	occupation: Occupation;
	month: string;
}

/** Por debajo de esta diferencia se considera que las horas cuadran. */
const HOURS_EPSILON = 0.01;

const round2 = (num: number): number => Math.round(num * 100) / 100;
const signed = (num: number): string => (num > 0 ? "+" : "") + round2(num);

const MISMATCH_FG = "#a15c00";

/**
 * Horas del mes por persona, en las tres fases por las que pasan:
 *
 *  1. PLANIFICADO — lo que la persona debía trabajar según su jornada y sus ausencias.
 *  2. IMPUTADO    — lo que apuntó en Redmine. Es la materia prima, no el resultado.
 *  3. DEDICADO    — lo que quedó tras la fase de distribución, donde el mánager agrupa y reparte
 *                   esas imputaciones por proyecto y modalidad (T&M, presupuesto...).
 *
 * El descuadre se mide entre planificado y **dedicado**, que es el dato validado. Comparar contra
 * lo imputado da falsas alarmas: quien no usa Redmine sale a cero aunque tenga todas sus horas
 * repartidas, y las correcciones que hace el mánager al distribuir aparecen como diferencias.
 */
export const OccupationImputation = ({occupation, month}: OccupationImputationProps) => {

	const people: OccupationPersonMonth[] = occupation.peopleByMonth?.[month] || [];
	const hasImputedData = (occupation.monthsWithImputedData || []).includes(Number(month));

	if (people.length === 0) {
		return null;
	}

	const rows = people
		.map((person) => {
			const planned = round2(person.availableHours || 0);
			const dedicated = round2(person.workedHours || 0);
			const imputed = round2(person.imputedHours || 0);
			return {person, planned, dedicated, imputed, diff: round2(dedicated - planned)};
		})
		.sort((a, b) => a.diff - b.diff);

	const mismatched = rows.filter((r) => Math.abs(r.diff) >= HOURS_EPSILON);

	const totalPlanned = round2(rows.reduce((acc, r) => acc + r.planned, 0));
	const totalDedicated = round2(rows.reduce((acc, r) => acc + r.dedicated, 0));

	return (
		<Box mt={3}>
			<Alert severity={mismatched.length > 0 ? "warning" : "success"}>
				<AlertTitle>Horas del mes</AlertTitle>
				{mismatched.length === 0
					? `Las ${rows.length} personas del mes tienen sus horas repartidas (${totalDedicated} h dedicadas).`
					: `${mismatched.length} de ${rows.length} personas no cuadran. `
					+ `Planificado ${totalPlanned} h frente a ${totalDedicated} h dedicadas.`}
			</Alert>

			{mismatched.length > 0 && (
				<>
					<Typography variant="subtitle2" sx={{mt: 2}} gutterBottom>
						Personas cuyas dedicaciones no cuadran con lo planificado
					</Typography>
					<Table size="small" className={"table-calendar"} sx={{maxWidth: 760}}>
						<TableHead>
							<TableRow>
								<TableCell>PERSONA</TableCell>
								<TableCell align="right">PLANIFICADO</TableCell>
								<TableCell align="right">DEDICADO</TableCell>
								<TableCell align="right" title={"Horas apuntadas en Redmine, antes de distribuir"}>
									IMPUTADO
								</TableCell>
								<TableCell align="right">DIF</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{mismatched.map((row) => (
								<TableRow key={row.person.personId}>
									<TableCell>{row.person.personName}</TableCell>
									<TableCell align="right">{row.planned}</TableCell>
									<TableCell align="right">{row.dedicated}</TableCell>
									<TableCell align="right" sx={{color: "text.disabled"}}>
										{hasImputedData ? row.imputed : "—"}
									</TableCell>
									<TableCell align="right" sx={{color: MISMATCH_FG, fontWeight: 600}}>
										{signed(row.diff)}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
					<Typography variant="caption" color="text.secondary" sx={{display: "block", mt: 1}}>
						La diferencia se mide contra lo <b>dedicado</b>, que es lo que queda tras distribuir.
						Lo imputado en Redmine se muestra sólo como referencia: es normal que no coincida.
					</Typography>
				</>
			)}
		</Box>
	);
};
